import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { getZonePendingOverview, approveZoneRequest, type ZonePendingEntry } from '../hooks/useIAM';
import ListStateBoundary from './iam/ListStateBoundary';
import { formatRequestedAt } from '../utils/formatDate';

interface ZoneApprovalsOverviewProps {
  onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

/**
 * Consolidated "who needs approval in each zone" admin view -- every zone's
 * pending self-service access requests shown side-by-side, with a one-click
 * Approve per request, instead of opening each group's edit page one at a
 * time to check for requests.
 */
const ZoneApprovalsOverview: React.FC<ZoneApprovalsOverviewProps> = ({ onShowToast }) => {
  const [zones, setZones] = useState<ZonePendingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getZonePendingOverview();
      setZones(data);
    } catch {
      setError('Failed to load pending zone requests');
      onShowToast('Failed to load pending zone requests', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [onShowToast]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const handleApprove = async (zone: string, username: string) => {
    const key = `${zone}:${username}`;
    setApproving(key);
    try {
      await approveZoneRequest(zone, username);
      onShowToast(`Added "${username}" to "${zone}"`, 'success');
      await refetch();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : `Failed to approve "${username}"`;
      onShowToast(message, 'error');
    } finally {
      setApproving(null);
    }
  };

  const totalPending = zones.reduce((sum, z) => sum + z.pending.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          IAM &gt; Zone Approvals
          {totalPending > 0 && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
              {totalPending} pending
            </span>
          )}
        </h2>
        <button
          onClick={() => {
            void refetch();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          title="Refresh"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Everyone currently waiting on approval to join a zone, grouped by zone. Approving adds
        the user to that zone on top of their existing groups -- it never removes anything.
      </p>

      <ListStateBoundary
        isLoading={isLoading}
        error={error}
        isEmpty={zones.length === 0}
        emptyMessage="No zones yet. Mark a group as a zone from IAM > Groups."
      >
        <div className="overflow-x-auto">
          <div className="flex gap-4 min-w-max pb-2">
            {zones.map((zoneEntry) => (
              <div
                key={zoneEntry.zone}
                className="w-64 flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-lg flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {zoneEntry.zone}
                  </span>
                  {zoneEntry.pending.length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                      {zoneEntry.pending.length}
                    </span>
                  )}
                </div>
                <ul className="p-3 space-y-3 min-h-[3rem]">
                  {zoneEntry.pending.length === 0 ? (
                    <li className="text-xs text-gray-400 dark:text-gray-500 italic">
                      Nothing pending
                    </li>
                  ) : (
                    zoneEntry.pending.map((req) => {
                      const key = `${zoneEntry.zone}:${req.requested_by}`;
                      const requestedAt = formatRequestedAt(req.requested_at);
                      return (
                        <li key={key} className="space-y-1.5">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {req.first_name || req.last_name
                              ? `${req.first_name ?? ''} ${req.last_name ?? ''}`.trim()
                              : req.requested_by}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                            {req.email ? (
                              <span>{req.email}</span>
                            ) : (
                              <span className="italic">{req.requested_by} (no email on file)</span>
                            )}
                          </div>
                          {requestedAt && (
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">
                              Requested {requestedAt.date} at {requestedAt.time}
                            </div>
                          )}
                          {req.reason && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              &ldquo;{req.reason}&rdquo;
                            </div>
                          )}
                          <button
                            onClick={() => handleApprove(zoneEntry.zone, req.requested_by)}
                            disabled={approving === key}
                            className="w-full px-2 py-1 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                          >
                            {approving === key ? 'Approving…' : 'Approve'}
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </ListStateBoundary>
    </div>
  );
};

export default ZoneApprovalsOverview;
